import { useEffect, useState } from 'react';
import { Backpack, Bell, CalendarPlus, Car, LogOut } from 'lucide-react';
import type { Me } from '../api/auth';
import { authApi } from '../api/auth';
import { calendarApi } from '../api/calendar';
import { notificationsApi } from '../api/notifications';
import { packingApi } from '../api/packing';
import { transportApi } from '../api/transport';
import { AppHeader } from '../ui/AppHeader';
import { Button } from '../ui/Button';
import { Footer, Gutter, Page } from '../ui/Chrome';
import { LinkRow } from '../ui/LinkRow';

/**
 * What follows you rather than the unit — TROOP-120.
 *
 * **Why it is not unit-scoped.** A calendar feed spans every unit a person touches, a car and a
 * licence follow their owner between units, and a personal packing list follows a Scout when they
 * move. All three were already outside `/units/` in the API for exactly that reason; they were in
 * the header because there was nowhere else to put them. This is the somewhere.
 *
 * **The same fact table as Manage**, and that is the point of building both at once: one is the
 * unit's lists and one is yours, and a reader should be able to tell them apart by the heading
 * rather than by the layout. Each row answers rather than labels — *subscribed* or not, how many
 * cars, how many of your own lists — because `DocketRow`'s rule applies wherever this row shape
 * does.
 *
 * **Sign out sits apart, below a rule, and is a button rather than a row.** It is the one control
 * here that does something instead of going somewhere, and putting it in the same column as three
 * navigation rows is how somebody taps it looking for their car.
 *
 * **Your own packing lists live on the unit's screen, reached from here.** That screen already
 * returns the unit's lists, your subgroups' and your own in one payload and writes each on its own
 * terms, so a second personal-only screen would be a second view of one endpoint — and the count
 * on this row comes from `/me/packing-templates/`, which is genuinely yours and unit-free. The row
 * is absent for somebody holding no membership, because the screen it opens is under `/units/` and
 * would 404 them: a control that cannot work does not render, rather than rendering greyed.
 */

interface Facts {
  subscribed?: boolean | null;
  vehicles?: number | null;
  seats?: number | null;
  myLists?: number | null;
  notifying?: number | null;
  notifyingOf?: number | null;
}

export function SettingsScreen({
  me,
  onNavigate,
}: {
  me: Me;
  onNavigate: (path: string) => void;
}) {
  const [facts, setFacts] = useState<Facts>({});
  const [signingOut, setSigningOut] = useState(false);

  // Where a personal list is edited. Any membership will do -- the screen shows the same personal
  // templates whichever unit's door you come through, because they belong to no unit.
  const anyUnit = me.memberships[0]?.unitId;

  useEffect(() => {
    let alive = true;

    // Settled one at a time rather than as a batch, for the reason `ManageScreen` gives: holding
    // every row's answer until the slowest read lands is how a page that promised to be no slower
    // to use spends two seconds showing placeholders.
    const settle = <T,>(promise: Promise<T>, onValue: (value: T) => Facts) =>
      promise
        .then((value) => {
          if (alive) setFacts((current) => ({ ...current, ...onValue(value) }));
        })
        .catch(() => undefined);

    void settle(calendarApi.status(), (status) => ({ subscribed: status.active }));

    void settle(transportApi.myVehicles(), ({ vehicles }) => {
      const live = vehicles.filter((v) => !v.retiredAt);

      return {
        vehicles: live.length,
        // Passenger seats, never including the driver -- the whole product counts them that way,
        // and a settings row that quietly added one would be the off-white lie that fires the
        // two-in-a-car advisory on every ordinary saloon.
        seats: live.reduce((total, v) => total + v.seatingCapacity, 0),
      };
    });

    void settle(packingApi.mine(), (lists) => ({ myLists: lists.length }));

    void settle(notificationsApi.settings(), (settings) => ({
      notifying: settings.categories.filter((c) => c.delivers).length,
      notifyingOf: settings.categories.length,
    }));

    return () => {
      alive = false;
    };
  }, []);

  async function signOut() {
    setSigningOut(true);
    try {
      await authApi.signOut();
    } finally {
      // Full reload rather than a route change: it clears every piece of in-memory state at once,
      // so nothing from the previous session is left rendered on a shared family device.
      window.location.href = '/';
    }
  }

  return (
    <Page>
      <AppHeader me={me} onNavigate={onNavigate} />

      <section className="rule-canonical">
        <Gutter>
          <div style={{ paddingBlock: 'var(--space-6)' }}>
            <p className="t-eyebrow" style={{ color: 'var(--color-text-muted)', margin: 0 }}>
              {me.displayName}
            </p>
            <h1
              className="t-heading"
              style={{
                color: 'var(--color-text-primary)',
                margin: 'var(--space-2) 0 var(--space-2)',
              }}
            >
              Your settings
            </h1>
            <p className="t-body" style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
              The things that follow you rather than the unit &mdash; across every unit you&rsquo;re in.
            </p>
          </div>
        </Gutter>
      </section>

      <Gutter>
        <div
          className="flex flex-col"
          style={{ paddingBlock: 'var(--space-6)', gap: 'var(--space-7)' }}
        >
          <section>
            <LinkRow
              label="Calendar"
              icon={CalendarPlus}
              value={
                facts.subscribed == null
                  ? 'Calendar feed'
                  : facts.subscribed
                    ? 'Subscribed'
                    : 'Not subscribed'
              }
              secondary="One feed covering every unit you're in, for the calendar app you already use."
              onClick={() => onNavigate('/calendar')}
            />

            {/* The value is the answer rather than the category (DocketRow's rule): how many of
                the six are on, so somebody who turned things off months ago can see it without
                spending the tap. "Notifications" alone would have moved that behind one. */}
            <LinkRow
              label="Alerts"
              icon={Bell}
              value={
                facts.notifying == null
                  ? 'Notifications'
                  : `${facts.notifying} of ${facts.notifyingOf} categories on`
              }
              secondary="What reaches you when you're not looking, across every unit you're in."
              onClick={() => onNavigate('/settings/notifications')}
            />

            <LinkRow
              label="Driving"
              icon={Car}
              value={
                facts.vehicles == null
                  ? 'Your car'
                  : facts.vehicles === 0
                    ? 'No car yet'
                    : `${facts.vehicles} ${facts.vehicles === 1 ? 'car' : 'cars'}, ${facts.seats} ${
                        facts.seats === 1 ? 'seat' : 'seats'
                      }`
              }
              secondary="Passenger seats, never counting you. Offer them on a trip when you're driving."
              onClick={() => onNavigate('/driving')}
            />

            {anyUnit && (
              <LinkRow
                label="Bring"
                icon={Backpack}
                value={
                  facts.myLists == null
                    ? 'Your lists'
                    : `${facts.myLists} of your own ${facts.myLists === 1 ? 'list' : 'lists'}`
                }
                secondary="Saved lists that are yours, not the unit's. They follow you between units."
                onClick={() => onNavigate(`/units/${anyUnit}/packing-lists`)}
              />
            )}
          </section>

          {/* Apart from the rows above, and a button rather than one of them: it acts instead of
              going somewhere. Sharing their column is how somebody taps it looking for their car. */}
          <section
            style={{
              borderTop: '1px solid var(--color-border-subtle)',
              paddingTop: 'var(--space-6)',
            }}
          >
            <Button
              variant="secondary"
              onClick={() => void signOut()}
              disabledReason={signingOut ? 'Signing out...' : undefined}
            >
              <LogOut size={16} aria-hidden="true" />
              Sign out
            </Button>
          </section>
        </div>
      </Gutter>

      <Footer />
    </Page>
  );
}
